import type { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import type { SocialNetworkBlock as SocialNetworkBlockValue } from "@reactivated";

/**
 * Faithful port of the legacy server-rendered
 * ``cms/blocks/flagship/social_networks.html`` (FlagshipPage
 * ``SocialNetworkBlock``).
 *
 * The ``.social`` wrapper + the per-site link grid move into React. The legacy
 * mapped each ``name`` choice to an inline SVG via
 * ``{% include 'cms/components/svg.html' with class="social__icon" src='...' %}``.
 * Only ``class`` + ``src`` were passed, so the svg.html ``title`` / ``title_id``
 * / ``ariaHidden`` / ``description`` branches were all empty -- the rendered
 * ``<svg>`` carries only ``class`` + ``role="img"`` + the per-icon
 * ``width``/``height``/``viewBox`` and a single ``<path>``. Those exact sprites
 * (``svg-django/{facebook-f,pinterest-p,X-logo,instagram,youtube-play}.svg``)
 * are reproduced inline below; only the 5 known names emit an icon (an unknown
 * name renders an empty ``<a>``), matching the legacy ``{% if name == ... %}``
 * chain. The ``twitter`` choice maps to the ``X-logo`` sprite, as in the legacy.
 *
 * The headline ``{% trans "Stay Updated &mdash; Connect With Us!" %}`` contained
 * a literal ``&mdash;`` HTML entity in the source string; reproduced here with
 * the unicode em-dash so the rendered text matches.
 */

const ICON_DZ: Record<string, React.JSX.Element | undefined> = {
    facebook: (
        <svg
            className="social__icon"
            role="img"
            xmlns="http://www.w3.org/2000/svg"
            width="1792"
            height="1792"
            viewBox="0 0 1792 1792"
        >
            <path d="M1343 12v264h-157q-86 0-116 36t-30 108v189h293l-39 296h-254v759H734V905H479V609h255V391q0-186 104-288.5T1115 0q147 0 228 12" />
        </svg>
    ),
    pinterest: (
        <svg
            className="social__icon"
            role="img"
            xmlns="http://www.w3.org/2000/svg"
            width="1792"
            height="1792"
            viewBox="0 0 1792 1792"
        >
            <path d="M256 597q0-108 37.5-203.5T397 227t152-123 185-78T936 0q158 0 294 66.5T1451 260t85 287q0 96-19 188t-60 177-100 149.5-145 103-189 38.5q-68 0-135-32t-96-88q-10 39-28 112.5t-23.5 95-20.5 71-26 71-32 62.5-46 77.5-62 86.5l-14 5-9-10q-15-157-15-188 0-92 21.5-206.5T604 972t52-203q-32-65-32-169 0-83 52-156t132-73q61 0 95 40.5T937 514q0 66-44 191t-44 187q0 63 45 104.5t109 41.5q55 0 102-25t78.5-68 56-95 38-110.5 20-111 6.5-99.5q0-173-109.5-269.5T909 163q-200 0-334 129.5T441 621q0 44 12.5 85t27 65 27 45.5T520 847q0 28-15 73t-37 45q-2 0-17-3-51-15-90.5-56t-61-94.5-32.5-108T256 597" />
        </svg>
    ),
    twitter: (
        <svg
            className="social__icon"
            role="img"
            xmlns="http://www.w3.org/2000/svg"
            xmlSpace="preserve"
            viewBox="0 0 300 271"
        >
            <path
                d="M236 0h46L181 115l118 156h-92.6l-72.5-94.8-83 94.8h-46l107-123L-1.1 0h94.9l65.5 86.6zm-16.1 244h25.5L80.4 26H53z"
                className="st0"
            />
        </svg>
    ),
    instagram: (
        <svg
            className="social__icon"
            role="img"
            xmlns="http://www.w3.org/2000/svg"
            width="1792"
            height="1792"
            viewBox="0 0 1792 1792"
        >
            <path d="M1490 1426V778h-135q20 63 20 131 0 126-64 232.5T1137 1310t-240 62q-197 0-337-135.5T420 909q0-68 20-131H299v648q0 26 17.5 43.5T360 1487h1069q25 0 43-17.5t18-43.5m-284-533q0-124-90.5-211.5T897 594q-127 0-217.5 87.5T589 893t90.5 211.5T897 1192q128 0 218.5-87.5T1206 893m284-360V368q0-28-20-48.5t-49-20.5h-174q-29 0-49 20.5t-20 48.5v165q0 29 20 49t49 20h174q29 0 49-20t20-49m174-208v1142q0 81-58 139t-139 58H325q-81 0-139-58t-58-139V325q0-81 58-139t139-58h1142q81 0 139 58t58 139" />
        </svg>
    ),
    youtube: (
        <svg
            className="social__icon"
            role="img"
            xmlns="http://www.w3.org/2000/svg"
            width="1792"
            height="1792"
            viewBox="0 0 1792 1792"
        >
            <path d="M1280 896q0-37-30-54L738 522q-31-20-65-2-33 18-33 56v640q0 38 33 56 16 8 31 8 20 0 34-10l512-320q30-17 30-54m512 0q0 96-1 150t-8.5 136.5T1760 1330q-16 73-69 123t-124 58q-222 25-671 25t-671-25q-71-8-124.5-58T31 1330q-14-65-21.5-147.5T1 1046 0 896t1-150 8.5-136.5T32 462q16-73 69-123t124-58q222-25 671-25t671 25q71 8 124.5 58t69.5 123q14 65 21.5 147.5T1791 746t1 150" />
        </svg>
    ),
};

export const FlagshipSocialNetworkBlock = (
    props: Immutable<{ value: SocialNetworkBlockValue }>,
): React.JSX.Element => {
    const sites = props.value.sites ?? [];
    return (
        <div className="social">
            <div className="social__headline">
                <h1>{t`Stay Updated — Connect With Us!`}</h1>
            </div>
            <div className="social__services">
                {sites.map((item, i) => (
                    <a
                        className="social__service"
                        href={item.url ?? ""}
                        title={item.name ?? ""}
                        key={i}
                    >
                        {item.name ? ICON_DZ[item.name] : null}
                    </a>
                ))}
            </div>
        </div>
    );
};
